/**
 * IntakeChecklistPanel — the one checklist Livi shows for a pasted case overview
 * (narrative intake, Learning Loop 1.5).
 *
 * Livi calls intake_case_narrative; the backend checks every extracted fact
 * against the case and publishes this panel. Each line is one fact (a person,
 * a business, a Case Details value, a key date, a claim, a topic), numbered, with
 * its status. Saveable lines start ticked; lines that won't be saved (already on
 * file, would change a value on file, not allowed here, needs fixing) are shown
 * unticked and can't be ticked.
 *
 * Save → emits `intake_save` with the ticked keys and a plain message; AppShell
 * sends it as a visible chat message carrying metadata.intakeSelection, and the
 * backend saves exactly those (the ticked boxes are the confirmation).
 * Change → emits `copy_to_chat` with "Change item N: " so the user tells Livi
 * what to fix; Livi previews again.
 *
 * After the save the backend re-renders the panel with each line's result.
 * No ids or field names are shown — only the plain summaries the backend wrote.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Badge } from '../../design-system';
import styles from './IntakeChecklistPanel.module.css';

const STATUS_VARIANT = {
  new: 'info',
  possible_match: 'warning',
  already_on_file: 'default',
  would_replace: 'warning',
  not_allowed: 'default',
  invalid: 'error',
};

const RESULT_LABEL = {
  saved: ['Saved', 'success'],
  skipped: ['Skipped', 'default'],
  failed: ['Not saved', 'error'],
  not_attempted: ['Not attempted', 'warning'],
};

// The lines that start ticked: saveable, except one the server marks preselected=false — similar to something
// already on the case (a re-paste showed seven repeated claims all ticked — owner, dev check 2026-10-25).
function startTicked(lines) {
  return lines.filter((l) => l.saveable && l.preselected !== false).map((l) => l.key);
}

// How long the panel waits for the save's result before unlocking (Livi may never make the save call). Pressing Save
// again is safe: the server saves a checklist only once.
export const SAVE_WAIT_MS = 60000;

function IntakeChecklistPanel({
  intakeId, lines = [], visibility = '', saved = false, notice = '', revision = '', onInteraction,
}) {
  const [checked, setChecked] = useState(() => new Set(startTicked(lines)));
  const [submitted, setSubmitted] = useState(false);
  // The line whose Change was pressed: says where to finish it (the text goes to the chat box, which nothing on the
  // checklist pointed to — owner, dev check 2026-10-24).
  const [changing, setChanging] = useState(null);
  // The save's result didn't come back in time: unlocked, with a note (dev check 2026-10-24: a save that never
  // completed left the panel on "Saving…" with every button dead).
  const [stuck, setStuck] = useState(false);

  // A new preview (new intakeId) starts fresh: the starting ticks again.
  useEffect(() => {
    setChecked(new Set(startTicked(lines)));
    setSubmitted(false);
    setChanging(null);
    setStuck(false);
    // lines change identity on every render of the payload; the intake id is the identity of the checklist
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intakeId]);

  // A refused save re-shows this checklist with a new revision and a notice: unlock.
  useEffect(() => {
    if (revision) {
      setSubmitted(false);
      setStuck(false);
    }
  }, [revision]);

  useEffect(() => {
    if (!submitted || saved) return undefined;
    const timer = setTimeout(() => {
      setSubmitted(false);
      setStuck(true);
    }, SAVE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [submitted, saved]);

  const sections = useMemo(() => {
    const order = [];
    const bySection = {};
    lines.forEach((line) => {
      if (!bySection[line.section]) {
        bySection[line.section] = [];
        order.push(line.section);
      }
      bySection[line.section].push(line);
    });
    return order.map((name) => ({ name, lines: bySection[name] }));
  }, [lines]);

  const locked = saved || submitted;
  const keep = lines.filter((l) => l.saveable && checked.has(l.key));

  const toggle = (key) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleChange = (line) => {
    setChanging(line.key);
    if (onInteraction) onInteraction({ type: 'copy_to_chat', message: `Change item ${line.n}: ` });
  };

  const handleSave = () => {
    if (!keep.length || locked) return;
    const leftOut = lines.filter((l) => l.saveable && !checked.has(l.key)).map((l) => l.n);
    const plural = keep.length === 1 ? 'item' : 'items';
    const message = `Save the ${keep.length} checked ${plural} from the checklist`
      + (leftOut.length ? ` (left out: ${leftOut.join(', ')}).` : '.');
    setSubmitted(true);
    setStuck(false);
    if (onInteraction) {
      onInteraction({
        type: 'intake_save',
        intakeId,
        keep: keep.map((l) => l.key),
        dropped: lines.filter((l) => l.saveable && !checked.has(l.key)).map((l) => l.key),
        message,
      });
    }
  };

  return (
    <section className={styles.panel} aria-labelledby="intake-checklist-title">
      <header className={styles.header}>
        <h2 id="intake-checklist-title" className={styles.title}>
          {saved ? 'What was saved' : 'Review before saving'}
        </h2>
        <p className={styles.subtitle}>
          {saved
            ? 'Each line shows whether it was saved.'
            : "Untick anything that's wrong, or press Change to tell Livi. Nothing is saved until you press Save."}
        </p>
        {visibility && !saved && <p className={styles.notice}>{visibility}</p>}
      </header>

      {sections.map((section) => (
        <div key={section.name} className={styles.section}>
          <h3 className={styles.sectionTitle}>{section.name}</h3>
          <ul className={styles.list}>
            {section.lines.map((line) => {
              const [resultLabel, resultVariant] = RESULT_LABEL[line.result] || [];
              return (
                <li key={line.key} className={styles.line}>
                  <label className={styles.check}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={line.saveable && checked.has(line.key)}
                      disabled={!line.saveable || locked}
                      onChange={() => toggle(line.key)}
                      aria-label={`Save item ${line.n}`}
                    />
                    <span className={styles.number}>{line.n}.</span>
                  </label>
                  <div className={styles.body}>
                    <p className={styles.summary}>{line.summary}</p>
                    {line.note && <p className={styles.note}>{line.note}</p>}
                    {line.result_note && <p className={styles.note}>{line.result_note}</p>}
                    {changing === line.key && !saved && (
                      <p className={styles.changeHint} role="status">
                        Finish your change in the chat box and send it. Livi will update the checklist.
                      </p>
                    )}
                  </div>
                  <div className={styles.side}>
                    {resultLabel ? (
                      <Badge variant={resultVariant}>{resultLabel}</Badge>
                    ) : (
                      <Badge variant={STATUS_VARIANT[line.status] || 'default'}>{line.status_label}</Badge>
                    )}
                    {!saved && (
                      <button
                        type="button"
                        className={styles.secondaryBtn}
                        onClick={() => handleChange(line)}
                        disabled={submitted}
                        aria-label={`Change item ${line.n}`}
                      >
                        Change
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {!saved && notice && !submitted && <p className={styles.warning} role="alert">{notice}</p>}
      {!saved && stuck && (
        <p className={styles.warning} role="alert">
          The save hasn&apos;t come back yet. If Livi&apos;s reply says it didn&apos;t go through, press Save again.
          A checklist can only be saved once, so pressing it again is safe.
        </p>
      )}
      {!saved && (
        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleSave}
            disabled={!keep.length || locked}
          >
            {submitted ? 'Saving…' : `Save ${keep.length} selected`}
          </button>
        </footer>
      )}
    </section>
  );
}

export default IntakeChecklistPanel;
